import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from "@mui/material";
import { api } from "../apiClient";
import { t } from "../i18n";
import { dialogSx, fieldSx, primaryButtonSx, secondaryButtonSx } from "../uiStyles";

const MIN_LENGTH = 8;
const EMPTY = { current: "", next: "", repeat: "" };

// Lets a signed-in user change their own password (POST /change_password checks the current one).
export default function ChangePasswordDialog({ open, currentLocale, currentTheme = "light", onClose, onChanged }) {
  const en = currentLocale === "en";
  const isDark = currentTheme === "dark";
  const [form, setForm] = useState(EMPTY);
  const [touched, setTouched] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return;
    setForm(EMPTY);
    setTouched(false);
    setError("");
  }, [open]);

  const tooShort = form.next.length > 0 && form.next.length < MIN_LENGTH;
  const mismatch = form.repeat.length > 0 && form.repeat !== form.next;
  const sameAsCurrent = form.next.length > 0 && form.next === form.current;
  const invalid = !form.current || form.next.length < MIN_LENGTH || form.repeat !== form.next || sameAsCurrent;
  const set = (key) => (event) => {
    setForm((value) => ({ ...value, [key]: event.target.value }));
    if (key === "current") setError("");
  };

  const save = async (event) => {
    event?.preventDefault();
    setTouched(true);
    if (invalid || saving) return;
    setSaving(true);
    setError("");
    try {
      await api.post("/change_password", { oldPassword: form.current, newPassword: form.next });
      onChanged?.();
      onClose?.();
    } catch (e) {
      setError(e?.response?.data?.error || t("password.changeFailed", currentLocale));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} fullWidth maxWidth="sm" sx={dialogSx}>
      <Box component="form" onSubmit={save} noValidate>
        <DialogTitle sx={{ pb: 1.25 }}>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>
            {t("password.title", currentLocale)}
          </Typography>
          <Typography variant="body2" sx={{ color: "text.secondary", mt: 0.75 }}>
            {en
              ? `Enter your current password and a new one of at least ${MIN_LENGTH} characters`
              : `Введите текущий пароль и новый — не короче ${MIN_LENGTH} символов`}
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ display: "grid", gap: 2, pt: "8px !important" }}>
          {error && <Alert severity="error">{error}</Alert>}
          <TextField
            type="password"
            autoComplete="current-password"
            autoFocus
            required
            label={t("password.current", currentLocale)}
            value={form.current}
            onChange={set("current")}
            error={touched && !form.current}
            fullWidth
            sx={{ ...fieldSx(isDark), mb: 2.5 /* separates the current password from the new pair */ }}
          />
          <TextField
            type="password"
            autoComplete="new-password"
            required
            label={t("password.new", currentLocale)}
            value={form.next}
            onChange={set("next")}
            error={tooShort || sameAsCurrent || (touched && form.next.length < MIN_LENGTH)}
            helperText={
              sameAsCurrent
                ? en ? "The new password matches the current one" : "Новый пароль совпадает с текущим"
                : tooShort || (touched && form.next.length < MIN_LENGTH)
                  ? en ? `At least ${MIN_LENGTH} characters` : `Минимум ${MIN_LENGTH} символов`
                  : ""
            }
            fullWidth
            sx={fieldSx(isDark)}
          />
          <TextField
            type="password"
            autoComplete="new-password"
            required
            label={t("password.repeat", currentLocale)}
            value={form.repeat}
            onChange={set("repeat")}
            error={mismatch || (touched && form.repeat !== form.next)}
            helperText={mismatch || (touched && form.repeat !== form.next)
              ? en ? "Passwords do not match" : "Пароли не совпадают"
              : ""}
            fullWidth
            sx={fieldSx(isDark)}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, pt: 0 }}>
          <Box sx={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 1.25, width: "100%" }}>
            <Button onClick={onClose} disabled={saving} sx={secondaryButtonSx}>
              {t("common.cancel", currentLocale)}
            </Button>
            <Button type="submit" variant="contained" disabled={saving} sx={primaryButtonSx}>
              {t("common.save", currentLocale)}
            </Button>
          </Box>
        </DialogActions>
      </Box>
    </Dialog>
  );
}
